import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent,
} from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { TruncatedText } from "../TruncatedText";
import {
  fieldDisabledClass,
  fieldFocusVisibleClass,
  fieldInvalidClass,
  fieldPopoverClass,
  fieldRadius,
} from "../field-variants";
import { type ContactVerifyStatus, verifyStatusBorderClass } from "../verifyStatus";
import type { CountryCode } from "./types";

export interface PhoneCountrySelectOption {
  value?: CountryCode;
  label: string;
  divider?: boolean;
}

export interface PhoneCountryIconProps {
  country?: CountryCode;
  label: string;
  aspectRatio?: number;
  "aria-hidden"?: boolean;
}

export interface PhoneCountrySelectProps {
  value?: CountryCode;
  onChange: (value: CountryCode | undefined) => void;
  options: PhoneCountrySelectOption[];
  disabled?: boolean;
  readOnly?: boolean;
  iconComponent: ComponentType<PhoneCountryIconProps>;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  error?: boolean;
  /** Same verification border as the number input (ignored when `error`). */
  verifyStatus?: ContactVerifyStatus;
  "aria-label"?: string;
  name?: string;
}

function isSameOptionValue(
  optionValue: CountryCode | undefined,
  selected: CountryCode | undefined,
): boolean {
  return optionValue === selected;
}

function getSelectedOption(
  options: PhoneCountrySelectOption[],
  value: CountryCode | undefined,
): PhoneCountrySelectOption | undefined {
  for (const option of options) {
    if (!option.divider && isSameOptionValue(option.value, value)) {
      return option;
    }
  }
  return undefined;
}

export function PhoneCountrySelect({
  value,
  onChange,
  options,
  disabled = false,
  readOnly = false,
  iconComponent: Icon,
  onFocus,
  onBlur,
  className,
  error = false,
  verifyStatus,
  "aria-label": ariaLabel,
}: PhoneCountrySelectProps) {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const isDisabled = disabled || readOnly;

  const selectableOptions = useMemo(
    () => options.filter((option) => !option.divider),
    [options],
  );

  const selectableIndices = useMemo(
    () =>
      options.reduce<number[]>((indices, option) => {
        const previous = indices.length > 0 ? indices[indices.length - 1] : -1;
        indices.push(option.divider ? -1 : previous + 1);
        return indices;
      }, []),
    [options],
  );

  const listItems = useMemo(
    () =>
      options.map((option, index) => ({
        option,
        index,
        selectableIndex: selectableIndices[index],
      })),
    [options, selectableIndices],
  );

  const selectedOption = useMemo(
    () => getSelectedOption(options, value),
    [options, value],
  );

  const closeList = useCallback(() => {
    setIsOpen((wasOpen) => {
      if (wasOpen) {
        onBlur?.();
      }
      return false;
    });
    setHighlightIndex(-1);
  }, [onBlur]);

  const closeListAndFocusTrigger = useCallback(() => {
    closeList();
    triggerRef.current?.focus();
  }, [closeList]);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeList();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [closeList]);

  const selectCountry = useCallback(
    (country: CountryCode | undefined) => {
      onChange(country);
      closeListAndFocusTrigger();
    },
    [onChange, closeListAndFocusTrigger],
  );

  const openList = () => {
    if (isDisabled) return;
    setIsOpen(true);
    onFocus?.();
    const selectedIndex = selectableOptions.findIndex((option) =>
      isSameOptionValue(option.value, value),
    );
    setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (isDisabled) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (isOpen) {
        if (
          highlightIndex >= 0 &&
          highlightIndex < selectableOptions.length
        ) {
          selectCountry(selectableOptions[highlightIndex].value);
        } else {
          closeListAndFocusTrigger();
        }
      } else {
        openList();
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!isOpen) {
        openList();
        return;
      }
      setHighlightIndex((index) =>
        index < selectableOptions.length - 1 ? index + 1 : 0,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        openList();
        return;
      }
      setHighlightIndex((index) =>
        index > 0 ? index - 1 : selectableOptions.length - 1,
      );
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeListAndFocusTrigger();
    }
  };

  const handleListKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (!isOpen || selectableOptions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightIndex((index) =>
        index < selectableOptions.length - 1 ? index + 1 : 0,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightIndex((index) =>
        index > 0 ? index - 1 : selectableOptions.length - 1,
      );
      return;
    }

    if (event.key === "Enter" && highlightIndex >= 0) {
      event.preventDefault();
      const option = selectableOptions[highlightIndex];
      if (option) {
        selectCountry(option.value);
      }
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeListAndFocusTrigger();
    }
  };

  return (
    <div ref={containerRef} className={cn("relative shrink-0", className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        disabled={isDisabled}
        className={cn(
          "flex h-9 min-h-9 items-center gap-1.5 border border-surface-200 bg-surface-0 py-0 pl-2.5 pr-2 shadow-xs transition-[border-color,box-shadow] duration-150 hover:border-surface-300 dark:bg-surface-50/60",
          fieldRadius.control,
          fieldFocusVisibleClass,
          fieldInvalidClass,
          fieldDisabledClass,
          verifyStatusBorderClass(verifyStatus, error),
          isOpen && "border-primary-500 ring-[3px] ring-primary-500/15",
        )}
        aria-invalid={error ? "true" : "false"}
        onClick={() => {
          if (isOpen) {
            closeListAndFocusTrigger();
          } else {
            openList();
          }
        }}
        onKeyDown={handleTriggerKeyDown}
      >
        <Icon
          aria-hidden
          country={value}
          label={selectedOption?.label ?? ""}
        />
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-surface-400 transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen ? (
        <ul
          id={listboxId}
          role="listbox"
          tabIndex={-1}
          className={fieldPopoverClass(
            "absolute left-0 z-50 mt-1 max-h-60 min-w-64 overflow-auto py-1",
          )}
          onKeyDown={handleListKeyDown}
        >
          {listItems.map(({ option, index, selectableIndex }) => {
            if (option.divider) {
              return (
                <li
                  key={`divider-${index}`}
                  role="separator"
                  className="my-1 border-t border-surface-200"
                />
              );
            }

            const isSelected = isSameOptionValue(option.value, value);
            const isHighlighted = selectableIndex === highlightIndex;

            return (
              <li
                key={option.value ?? "international"}
                role="option"
                aria-selected={isSelected}
                className={cn(
                  "flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-surface-900",
                  isHighlighted && "bg-primary-50",
                  "hover:bg-surface-50",
                )}
                onMouseEnter={() => setHighlightIndex(selectableIndex)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectCountry(option.value)}
              >
                <Icon
                  aria-hidden
                  country={option.value}
                  label={option.label}
                />
                <TruncatedText>{option.label}</TruncatedText>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

